import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Container from "@mui/material/Container";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import IconButton from "@mui/material/IconButton";
import {
  ArrowBack,
  LocalTaxi,
  CalendarMonth,
  Route as RouteIcon,
  Description,
  Policy,
  CheckCircleOutline,
  ArrowBackIosNew,
  ArrowForwardIos
} from "@mui/icons-material";
import { db } from "../firebaseConfig";
import { doc, getDoc } from "firebase/firestore";
import { DestImgData } from "../Constants";
import Navbar from "../Navbar";
import MyDailog from "../Commons/myDailog.jsx";
import BookingForm from "./BookingForm";
import { devConsolelog } from "../Utility.js";
import PopUp from "../Commons/messagePopUp";

const PackageDetails = () => {
  const { pkgId } = useParams();
  const navigate = useNavigate();
  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCab, setSelectedCab] = useState(0);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [currentImgIndex, setCurrentImgIndex] = useState(0);
  const scrollRef = useRef(null);
  const [bookingSubmit, setBookingSubmit] = useState(null);

  const currencySymbol = pkg?.currency === "INR" ? "₹" : `${pkg?.currency} `;
  // const normalize = (str) => str?.toLowerCase().replace(/_/g, '-').trim();
  const destImgItem = DestImgData[pkg?.destination.toLowerCase()];
  const images = destImgItem?.imgList || [''];
  devConsolelog("pkg imgs ", images);

  useEffect(() => {
    const fetchPackage = async () => {
      try {
        const docRef = doc(db, "transportPackages", pkgId);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          setPkg(docSnap.data());
        } else {
          console.error("No such package!");
        }
      } catch (error) {
        console.error("Error fetching package:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPackage();
  }, [pkgId]);

  useEffect(() => {
    if (images && images.length > 1) {
      const interval = setInterval(() => {
        setCurrentImgIndex((prev) => (prev + 1) % images.length);
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [images]);

  useEffect(() => {
    if (scrollRef.current) {
      const container = scrollRef.current;
      const imageWidth = container.children[0]?.offsetWidth || 0;
      container.scrollTo({
        left: currentImgIndex * imageWidth,
        behavior: "smooth",
      });
    }
  }, [currentImgIndex]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "80vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!pkg) {
    return (
      <Box sx={{ textAlign: "center", py: 10 }}>
        <Typography variant="h5">Package not found</Typography>
        <Button onClick={() => navigate("/packages")} sx={{ mt: 2 }}>
          Back to Packages
        </Button>
      </Box>
    );
  }

  return (
    <>
      <Navbar />
      <Box sx={{ pt: 2, pb: 8, minHeight: "100vh" }}>
        <Container maxWidth="lg">
          <Breadcrumbs sx={{ mb: 3 }}>
            <Link underline="hover" color="primary" onClick={() => navigate("/packages")} style={{ cursor: "pointer" }}>
              Packages
            </Link>
            <Typography color="text.primary">{pkg.title}</Typography>
          </Breadcrumbs>

          <Grid container spacing={2}>
            {/* Left Column: Main Details */}
            <Grid item xs={12} md={8}>
              <Card sx={{ borderRadius: 4, overflow: "hidden", boxShadow: "0 4px 20px rgba(0,0,0,0.08)" }}>
                <Box sx={{ position: "relative", overflow: "hidden" }}>
                  <Box
                    ref={scrollRef}
                    sx={{
                      display: "flex",
                      overflowX: "auto",
                      height: 300,
                      gap: 1,
                      "&::-webkit-scrollbar": { display: "none" },
                      msOverflowStyle: "none",
                      scrollbarWidth: "none",
                    }}
                  >
                    {images.map((img, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          minWidth: { xs: "100%", md: 400 },
                          height: "100%",
                          width: "100%",
                          backgroundImage: `url(${img})`,
                          backgroundSize: "cover",
                          backgroundPosition: "center",
                          flexShrink: 0,
                        }}
                      />
                    ))}
                  </Box>

                  {images.length > 1 && (
                    <>
                      <IconButton
                        onClick={() => setCurrentImgIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1))}
                        sx={{
                          position: "absolute",
                          left: 10,
                          top: "50%",
                          transform: "translateY(-50%)",
                          bgcolor: "rgba(255,255,255,0.7)",
                          "&:hover": { bgcolor: "rgba(255,255,255,0.9)" },
                          width: 35,
                          height: 35,
                          p: 0,
                          zIndex: 2,
                        }}
                      >
                        <ArrowBackIosNew sx={{ fontSize: 18 }} />
                      </IconButton>
                      <IconButton
                        onClick={() => setCurrentImgIndex((prev) => (prev + 1) % images.length)}
                        sx={{
                          position: "absolute",
                          right: 10,
                          top: "50%",
                          transform: "translateY(-50%)",
                          bgcolor: "rgba(255,255,255,0.7)",
                          "&:hover": { bgcolor: "rgba(255,255,255,0.9)" },
                          width: 35,
                          height: 35,
                          p: 0,
                          zIndex: 2,
                        }}
                      >
                        <ArrowForwardIos sx={{ fontSize: 18 }} />
                      </IconButton>
                    </>
                  )}
                </Box>
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 3 }}>
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                        {pkg.title}
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip label={pkg.destination} color="primary" sx={{ fontWeight: 600 }} />
                        <Chip
                          label={pkg.type === "domestic" ? "Domestic" : "International"}
                          variant="outlined"
                          size="small"
                        />
                      </Stack>
                    </Box>
                    <Box sx={{ textAlign: "right" }}>
                      <Typography variant="caption" color="text.secondary">Starting from</Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: "primary.main" }}>
                        {currencySymbol}{Math.min(...pkg.cabPricing.map(p => p.price)).toLocaleString()}
                      </Typography>
                    </Box>
                  </Box>

                  <Divider sx={{ mb: 3 }} />

                  {/* Quick Info Grid */}
                  <Grid container spacing={2} sx={{ mb: 4 }}>
                    <Grid item xs={6} sm={4}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CalendarMonth color="action" />
                        <Box>
                          <Typography variant="caption" color="text.secondary">Duration</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{pkg.duration}</Typography>
                        </Box>
                      </Stack>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <RouteIcon color="action" />
                        <Box>
                          <Typography variant="caption" color="text.secondary">Route</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{pkg.route}</Typography>
                        </Box>
                      </Stack>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <LocalTaxi color="action" />
                        <Box>
                          <Typography variant="caption" color="text.secondary">Pickup & Drop</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{pkg.pickupDrop}</Typography>
                        </Box>
                      </Stack>
                    </Grid>
                  </Grid>

                  {/* Detailed Itinerary */}
                  <Box sx={{ mb: 4 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                      <Description color="primary" />
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>Detailed Itinerary</Typography>
                    </Box>
                    <Typography variant="body1" sx={{ whiteSpace: "pre-line", color: "text.secondary", lineHeight: 1.7 }}>
                      {pkg.detailedItinerary || pkg.itinerary}
                    </Typography>
                  </Box>

                  <Divider sx={{ mb: 3 }} />

                  {/* Policies */}
                  {/* <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                      <Policy color="primary" />
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>Policies & Terms</Typography>
                    </Box>
                    <Stack spacing={2}>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Cancellation Policy</Typography>
                        <Typography variant="body2" color="text.secondary">{pkg.policies || "No specific cancellation policy provided."}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Inclusions</Typography>
                        <Typography variant="body2" color="text.secondary">{pkg.priceInclusion || "Standard transport services."}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Exclusions</Typography>
                        <Typography variant="body2" color="text.secondary">{pkg.taxes || "Taxes and extra charges as applicable."}</Typography>
                      </Box>
                    </Stack>
                  </Box> */}
                </CardContent>
              </Card>
            </Grid>

            {/* Right Column: Pricing & Booking */}
            <Grid item xs={12} md={4}>
              <Card sx={{ borderRadius: 4, boxShadow: "0 4px 20px rgba(0,0,0,0.08)", position: "sticky", top: 100 }}>
                <CardContent sx={{ p: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Select Vehicle</Typography>

                  <Stack spacing={1}>
                    {pkg.cabPricing.map((cab, index) => (
                      <Box
                        key={index}
                        onClick={() => setSelectedCab(index)}
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          border: "2px solid",
                          borderColor: selectedCab === index ? "primary.main" : "divider",
                          bgcolor: selectedCab === index ? "action.hover" : "transparent",
                          transition: "all 0.2s",
                          "&:hover": { borderColor: "primary.main", bgcolor: "action.hover" },
                          cursor: "pointer"
                        }}
                      >
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                          <Typography sx={{ fontWeight: 600 }}>{cab.cabType}</Typography>
                          <Typography variant="body2" color="text.secondary">{cab.seats} Seater</Typography>
                        </Box>
                        <Typography variant="h6" sx={{ color: "primary.main", fontWeight: 700 }}>
                          {currencySymbol}{cab.price.toLocaleString()} <small>/ {pkg.priceInclusion} </small>
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.2 }}>
                          {pkg.priceType} | Taxes: ({pkg?.taxes})
                        </Typography>
                      </Box>
                    ))}
                  </Stack>

                  <Button
                    variant="contained"
                    fullWidth
                    size="large"
                    sx={{ mt: 2, py: 1.2, borderRadius: 2, fontWeight: 600 }}
                    onClick={() => setShowBookingModal(true)}
                  >
                    Book Now
                  </Button>
                  <Button
                    variant="outlined"
                    fullWidth
                    size="large"
                    sx={{ mt: 1, py: 1.2, borderRadius: 2 }}
                    onClick={() => navigate(`/post-req/${pkg.destination}/${pkgId}`)}
                  >
                    Customize Package
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>
      <MyDailog
        showDailog={showBookingModal}
        title={`Book Your Cab - ${pkg.title} (${pkg.cabPricing[selectedCab]?.cabType})`}
        handleClose={() => setShowBookingModal(false)}
        body={
          <BookingForm
            pkg={pkg}
            selectedCabIndex={selectedCab}
            handleClose={() => setShowBookingModal(false)}
            handleSubmit={setBookingSubmit}
          />
        }
      />
      {
        bookingSubmit?.bookingId && <PopUp
          open={Boolean(bookingSubmit?.bookingId)}
          checkMark={false}
          forwardRef={`/booking/${bookingSubmit?.bookingId}`}
          primaryText="Congratulations!"
          secondaryText="A booking is created for your requirement"
          secondaryText1="Please share Client & arrival details in the booking chat. Separate Button for Client contact number."
          secondaryText2="Driver details will be shared 1 day before pickup"
          secondaryText3="You will now be redirected to your booking details."
          submitText={"Chat with Supplier"}
        />
      }
    </>
  );
};

export default PackageDetails;
